import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}

/**
 * Hero sections render separate desktop and mobile videos. Some mobile browsers
 * grant autoplay to the first video in the DOM even when CSS hides it, leaving
 * the visible mobile video paused. Keep playback aligned with the active media
 * query and retry after page restoration or a viewport change.
 */
const syncHeroVideoPlayback = () => {
  for (const video of document.querySelectorAll('.hero video[autoplay]')) {
    const isVisible = video.getClientRects().length > 0 && getComputedStyle(video).visibility !== 'hidden';

    if (!isVisible) {
      video.pause();
      continue;
    }

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    if (video.paused) video.play().catch(() => {});
  }
};

const scheduleHeroVideoSync = () => requestAnimationFrame(syncHeroVideoPlayback);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleHeroVideoSync, { once: true });
} else {
  scheduleHeroVideoSync();
}

window.addEventListener('pageshow', scheduleHeroVideoSync);
window.addEventListener('resize', scheduleHeroVideoSync, { passive: true });
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) scheduleHeroVideoSync();
});
document.addEventListener('shopify:section:load', scheduleHeroVideoSync);
